import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban } from 'lucide-react'
import clsx from 'clsx'
import type { KillContactMethod, Loan } from '@/types'
import { loansService } from '@/services/loans'
import { LOAN_QUERY_KEYS, invalidateLoanQueries } from '@/services/loanQueryKeys'
import { formatTimestamp } from '@/utils/dates'
import { NO_DEAL_EDIT, useCanEditDeal } from './dossier/dealEditPermission'

/** Kill / reopen control. A killed deal is retained and shown as dead —
 *  never hidden and never deleted.
 *
 *  The one kill form: the Pipeline Inspector and the Deal Locator drawer both
 *  render it, so every kill carries the reason picklist, the note, the stage
 *  it died at and the broker attestation (randy-2.7). `onChanged` lets a host
 *  that is not driven by the loan queries refresh its own row. */
export type KillDealLoan = Pick<
  Loan,
  | 'id'
  | 'stage'
  | 'dealStatus'
  | 'killedAt'
  | 'killedAtStage'
  | 'killReasonCode'
  | 'killReasonNote'
  | 'killedBy'
  | 'killBrokerContacted'
  | 'killContactMethod'
  | 'killContactDate'
  | 'deadReason'
>

export function KillDealPanel({
  loan,
  onChanged,
}: {
  loan: KillDealLoan
  onChanged?: () => void
}) {
  const queryClient = useQueryClient()
  // Kill and reopen are deal writes (POST /loans/{id}/kill and /reopen require
  // pipeline:edit). A role without it reads the record, and is not offered a
  // form whose confirm the API would refuse.
  const canEditDeal = useCanEditDeal()
  const [open, setOpen] = useState(false)
  const [reasonCode, setReasonCode] = useState('')
  const [note, setNote] = useState('')
  const [reopenReason, setReopenReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  // F4/F5 broker-contact attestation: null until the operator answers yes/no.
  const [brokerContacted, setBrokerContacted] = useState<boolean | null>(null)
  const [contactMethod, setContactMethod] = useState<KillContactMethod | ''>('')
  const [contactDate, setContactDate] = useState('')

  // A dead deal read from the loan list: its status says so even if the kill
  // timestamp was never recorded (a kill predating the column), so the panel
  // offers to reopen it rather than to kill it again.
  const isKilled = Boolean(loan.killedAt) || (loan.dealStatus ?? '').toLowerCase() === 'dead'

  const reasonsQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.killReasons,
    queryFn: () => loansService.getKillReasons(),
    // The picklist is a fixed server-side vocabulary; no need to refetch often.
    staleTime: 5 * 60 * 1000,
    // Also when killed: the recorded code is shown by its picklist label.
    enabled: open || isKilled,
  })

  const reasons = reasonsQuery.data?.reasons ?? []
  const requireNote = reasonsQuery.data?.requireNote ?? []
  const contactMethods = (reasonsQuery.data?.contactMethods ?? ['call', 'email']) as KillContactMethod[]
  const noteRequired = reasonCode !== '' && requireNote.includes(reasonCode)
  // The attestation is complete when the operator has answered the yes/no, and —
  // if yes — named a valid method and an ISO contact date. Mirrors the server.
  const attestationComplete =
    brokerContacted === false ||
    (brokerContacted === true && contactMethod !== '' && contactDate.trim().length > 0)
  const canKill =
    reasonCode !== '' && (!noteRequired || note.trim().length > 0) && attestationComplete

  const reset = () => {
    setOpen(false)
    setReasonCode('')
    setNote('')
    setReopenReason('')
    setError(null)
    setBrokerContacted(null)
    setContactMethod('')
    setContactDate('')
  }

  const killMutation = useMutation({
    mutationFn: () =>
      loansService.kill(loan.id, {
        reasonCode,
        note: note.trim() || undefined,
        // Guarded by canKill: brokerContacted is non-null before this runs.
        brokerContacted: brokerContacted === true,
        contactMethod: brokerContacted === true ? (contactMethod as KillContactMethod) : undefined,
        contactDate: brokerContacted === true ? contactDate.trim() : undefined,
      }),
    onSuccess: async () => {
      reset()
      await invalidateLoanQueries(queryClient, loan.id)
      onChanged?.()
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Unable to kill this deal.')
    },
  })

  const reopenMutation = useMutation({
    mutationFn: () => loansService.reopen(loan.id, reopenReason.trim()),
    onSuccess: async () => {
      reset()
      await invalidateLoanQueries(queryClient, loan.id)
      onChanged?.()
    },
    onError: () => setError('Unable to reopen this deal.'),
  })

  if (isKilled) {
    // A kill recorded before the picklist was the only way in has no code;
    // its free-text reason is what the desk wrote, so it is shown instead. A
    // code that is no longer on the picklist (the housekeeping reset's) reads as
    // the label the kill recorded beside it, never as the bare code.
    const label =
      reasons.find((r) => r.code === loan.killReasonCode)?.label ||
      loan.deadReason ||
      loan.killReasonCode ||
      'No reason recorded'
    return (
      <div className="rounded-md border border-red-400/25 bg-red-500/[0.06] px-3 py-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-red-300">
          <Ban className="h-3.5 w-3.5" /> Deal killed
        </div>
        <div className="mt-1 text-[12px] text-stone-200">{label}</div>
        {loan.killReasonNote ? (
          <div className="mt-1 text-[11px] text-stone-400">{loan.killReasonNote}</div>
        ) : null}
        <div className="mt-1.5 space-y-0.5 text-[10px] text-stone-500">
          <div>Died at stage: {loan.killedAtStage || 'unknown'}</div>
          {/* A desk time, never the raw naive-UTC ISO string (nd-032). */}
          <div>
            {[loan.killedBy, formatTimestamp(loan.killedAt) || 'date not recorded'].filter(Boolean).join(' · ')}
          </div>
          {loan.killBrokerContacted != null ? (
            <div>
              Broker contacted:{' '}
              {loan.killBrokerContacted
                ? `Yes · ${loan.killContactMethod ?? '—'}${loan.killContactDate ? ` · ${loan.killContactDate}` : ''}`
                : 'No'}
            </div>
          ) : null}
        </div>

        {!open || !canEditDeal ? (
          <button
            type="button"
            onClick={() => {
              if (canEditDeal) setOpen(true)
            }}
            disabled={!canEditDeal}
            title={canEditDeal ? undefined : NO_DEAL_EDIT}
            className="mt-2 rounded border border-stone-600 px-2.5 py-1 text-[10px] text-stone-300 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Reopen deal
          </button>
        ) : (
          <div className="mt-2 space-y-1.5">
            <label className="block">
              <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Reason for reopening</span>
              <input
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Required reason"
                className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
                autoFocus
              />
            </label>
            {error ? <div className="text-[10px] text-red-300">{error}</div> : null}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!reopenReason.trim() || reopenMutation.isPending}
                onClick={() => reopenMutation.mutate()}
                className="rounded bg-amber-300 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-60"
              >
                {reopenMutation.isPending ? 'Reopening…' : 'Confirm reopen'}
              </button>
              <button
                type="button"
                onClick={reset}
                className="rounded border border-stone-600 px-2.5 py-1 text-[10px] text-stone-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (!open || !canEditDeal) {
    return (
      <button
        type="button"
        onClick={() => {
          if (canEditDeal) setOpen(true)
        }}
        disabled={!canEditDeal}
        title={canEditDeal ? undefined : NO_DEAL_EDIT}
        className="inline-flex items-center gap-1.5 rounded border border-red-400/25 px-3 py-1.5 text-[11px] text-red-300 hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <Ban className="h-3.5 w-3.5" /> Kill deal
      </button>
    )
  }

  return (
    <div className="w-full space-y-1.5 rounded-md border border-red-400/25 bg-red-500/[0.04] px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.14em] text-red-300">Kill deal</div>
      <label className="block">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Reason</span>
        <select
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          disabled={reasonsQuery.isLoading}
          className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
        >
          <option value="">
            {reasonsQuery.isLoading ? 'Loading reasons…' : 'Select a reason'}
          </option>
          {reasons.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
        </select>
      </label>
      {reasonsQuery.isError ? (
        <div className="text-[10px] text-red-300">Unable to load kill reasons.</div>
      ) : null}
      <label className="block">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">
          Note {noteRequired ? '(required)' : '(optional)'}
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder={noteRequired ? 'Explain why this deal died' : 'Context for the post-mortem'}
          className="mt-1 w-full resize-y rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
        />
      </label>
      {/* F4/F5 broker-contact attestation — required before a kill is allowed. */}
      <div className="rounded border border-stone-700/70 bg-black/20 px-2 py-1.5">
        <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">
          Was the broker told this deal is dead? (required)
        </span>
        <div className="mt-1 flex gap-2">
          {([['Yes', true], ['No', false]] as const).map(([label, value]) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setBrokerContacted(value)
                if (!value) {
                  setContactMethod('')
                  setContactDate('')
                }
              }}
              className={clsx(
                'flex-1 rounded border px-2 py-1 text-[11px]',
                brokerContacted === value
                  ? 'border-amber-300/60 bg-amber-300/15 text-amber-100'
                  : 'border-stone-700 text-stone-300 hover:bg-white/5',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        {brokerContacted === true ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Method</span>
              <select
                value={contactMethod}
                onChange={(e) => setContactMethod(e.target.value as KillContactMethod | '')}
                className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
              >
                <option value="">Select</option>
                {contactMethods.map((m) => (
                  <option key={m} value={m}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Contact date</span>
              <input
                type="date"
                value={contactDate}
                onChange={(e) => setContactDate(e.target.value)}
                className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
              />
            </label>
          </div>
        ) : null}
      </div>
      <div className="text-[10px] text-stone-500">
        Recorded at stage: {loan.stage}. The deal is retained and stays searchable.
      </div>
      {error ? <div className="text-[10px] text-red-300">{error}</div> : null}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!canKill || killMutation.isPending}
          onClick={() => killMutation.mutate()}
          className="rounded bg-red-400 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-60"
        >
          {killMutation.isPending ? 'Killing…' : 'Confirm kill'}
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded border border-stone-600 px-2.5 py-1 text-[10px] text-stone-300"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
